import { PAGE_ROUTES } from 'constants/pageRoutes';
import { COUNTRY_CODE_TO_LOCALE } from 'petdna-lib-uicomponents/constants';
import { useCountryCode } from 'petdna-lib-uicomponents/hooks';
import { ITopPagePromoBanner } from 'petdna-lib-uicomponents/types';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useStores } from 'stores/useStores';

import { useEntitlements } from './useEntitlements';

export const getPageIdentifier = (route: string | null) => {
  if (!route) return null;

  const path = route?.substring(1);
  const matchedRoute = PAGE_ROUTES.find(({ pattern }) => {
    if (pattern instanceof RegExp) {
      return pattern.test(path);
    }
    return pattern === path;
  });

  if (matchedRoute) {
    return matchedRoute.pageId;
  }

  return null;
};

export const useLoadPromoBanner = (enabled = true) => {
  const entitlements = useEntitlements();
  const { pagesStore } = useStores();
  const countryCode = useCountryCode(false);
  const location = useLocation();
  const pageId = useMemo(() => getPageIdentifier(location?.pathname), [location?.pathname]);

  useEffect(() => {
    if (!countryCode) return;
    pagesStore.getPromoBanners(enabled, [COUNTRY_CODE_TO_LOCALE[countryCode]]);
  }, [enabled, countryCode, pagesStore]);

  return useMemo(() => {
    const promoBanners = pagesStore?.promoBanners ?? [];

    const { currentPageBanners, globalBanners } = promoBanners.reduce((
      acc,
      banner,
    ) => {
      if (!banner.pageId) {
        acc.globalBanners.push(banner);
      } else if ((banner.pageId === pageId) && pageId) {
        acc.currentPageBanners.push(banner);
      }
      return acc;
    }, {
      currentPageBanners: [] as ITopPagePromoBanner[],
      globalBanners: [] as ITopPagePromoBanner[],
    });

    const selectedPromoBanner = currentPageBanners.length
      ? currentPageBanners[0] : (globalBanners[0] ?? null);
    const entitled = selectedPromoBanner?.entitlements.every((ent) => entitlements.has(ent));
    const promoBanner = entitled ? selectedPromoBanner : null;

    return { promoBanner };
  }, [entitlements, pageId, pagesStore?.promoBanners]);
};
